import React, { useLayoutEffect, useState } from "react";
import { useAppBlocksContext } from "../../context/AppBlocksContext";
import { useTableContext } from "../../context/TableContext";
import Tree from "../SideBar/Tree";
import { getFieldIcon } from "../../utils/getFieldIcon";
import Input from "../FormComponents/Input";
import MaterialIcon from "./MaterialIcon";
import { toastError } from "../../utils/toastUtils";
import { useAPIContext } from "../../context/APIContext";
import { useParams } from "react-router-dom";
import { collapseFolder } from "../../utils/treeData/folderEditorUtils";

// preferredElements is override excludedElements
const TableTreeModal = ({
  callbackFunction,
  tableId,
  folderId = [],
  preferredElements = [],
  excludedElements = ["spacer"],
  selectedFile = "",
  tableType,
  appendExtraFilesAndFolders = {
    tree: [],
    data: {}
  }
}) => {
  const { getTableTypeData } = useTableContext();
  const [tableTreeExplorer, setTableTreeExplorer] = useState([]);
  const [allFieldsObject, setAllFieldsObject] = useState({});
  const [isFolderCollapse, setIsFolderCollapse] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { applicationBlockData } = useAppBlocksContext();
  const { generateFieldObject, addDynamicExtraFolders, selectedApiData } = useAPIContext();
  const { apiId } = useParams();

  const tableTypeToFilter = tableType || applicationBlockData.table_type || "statemachine";

  const handleAllFolderCollapse = () => {
    const data = collapseFolder(tableTreeExplorer, !isFolderCollapse);
    setTableTreeExplorer(data);
    setIsFolderCollapse(!isFolderCollapse);
  };
  function filterFilesByTypeInFolder(dataArray) {
    function filterTreeByType(items) {
      if (!items) return;
      for (let i = items.length - 1; i >= 0; i--) {
        const item = items[i];
        if (item.isFolder && item.items.length > 0) {
          filterTreeByType(item.items);
        } else {
          if (preferredElements.length > 0 && !preferredElements.includes(item.type)) {
            items.splice(i, 1);
          } else if (excludedElements.includes(item.type)) {
            items.splice(i, 1);
          }
        }
      }
    }
    let i = 0;
    while (i < dataArray.length) {
      if (!dataArray[i].items) {
        dataArray.splice(i, 1);
      } else {
        filterTreeByType(dataArray[i].items);
        i++;
      }
    }
    return dataArray;
  }

  const getFolderFromIds = (folder, ids) => {
    if (ids.includes(folder.id)) {
      return folder;
    }
    let folderData = false;
    for (const items of folder.items) {
      if (items.isFolder) {
        const data = getFolderFromIds(items, ids);
        if (data) {
          folderData = data;
          break;
        }
      }
    }
    return folderData;
  };

  const fetchSingleData = async () => {
    try {
      const { treeData, tableObject } = await getTableTypeData(
        tableType || applicationBlockData.table_type || "statemachine",
        tableId
      );

      let updatedTreeData = [];

      if (Array.isArray(folderId) && folderId.length > 0) {
        for (const id of folderId) {
          for (const folder of treeData) {
            const data = getFolderFromIds(folder, [id]);
            if (data) {
              updatedTreeData.push(data);
              break;
            }
          }
        }
      } else {
        updatedTreeData = treeData;
      }

      if (preferredElements.length > 0 || excludedElements.length > 0) {
        const newData = filterFilesByTypeInFolder(updatedTreeData);
        updatedTreeData = newData;
      }

      if (selectedFile) {
        updatedTreeData = updateParentFolders(updatedTreeData, selectedFile);
      }

      if (selectedApiData?.id !== undefined && apiId !== undefined && selectedApiData.id === apiId) {
        // this condition for webhook api query,body, params data
        const dynamicFolders = addDynamicExtraFolders();
        const dynmaicFolderObject = generateFieldObject();
        setTableTreeExplorer([...dynamicFolders]);
        setAllFieldsObject({ ...dynmaicFolderObject });
        return;
      }

      setTableTreeExplorer([...updatedTreeData, ...appendExtraFilesAndFolders.tree]);
      setAllFieldsObject({ ...tableObject, ...appendExtraFilesAndFolders.data });
    } catch (error) {
      toastError(error.message);
    }
  };

  function updateParentFolders(treeData, fileId) {
    const copiedTreeData = JSON.parse(JSON.stringify(treeData));

    function findFolderAndOpenParents(folder, fileId) {
      if (!folder || !folder.items || !folder.items.length) {
        folder.isOpen = false;
        return false;
      }

      const containsFileId = folder.items.some((item) => item.id === fileId);
      const foundInParent = folder.items.some((item) => findFolderAndOpenParents(item, fileId));

      if (containsFileId || foundInParent) {
        folder.isOpen = true;
      } else {
        folder.isOpen = false;
      }

      return containsFileId || foundInParent;
    }

    copiedTreeData.forEach((folder) => findFolderAndOpenParents(folder, fileId));

    return copiedTreeData;
  }

  const handleFieldPath = () => {
    const newTreeData = updateParentFolders(tableTreeExplorer, selectedFile);
    setTableTreeExplorer(newTreeData);
  };

  const getSelectedFilePath = () => {
    if (!selectedFile) return "";
    const selectedFileObject = allFieldsObject[selectedFile];
    if (!selectedFileObject) {
      return <div>Field Not Found</div>;
    }

    return (
      <span className="d_flex align_center gap_2 cursor_pointer" onClick={handleFieldPath}>
        <MaterialIcon name={getFieldIcon(selectedFileObject.field_type || "")} className="folder_icon" />
        {selectedFileObject.prefix}.{selectedFileObject.display} [{selectedFileObject.field_type}]
      </span>
    );
  };

  const addingData = (id) => {
    callbackFunction(allFieldsObject[id]);
  };

  useLayoutEffect(() => {
    fetchSingleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewInsert = (data) => {
    callbackFunction(allFieldsObject[data.id]);
  };

  const filterItems = (items, query) => {
    return items
      .map((item) => {
        if (item.isFolder) {
          const filteredSubItems = filterItems(item.items, query);
          if (filteredSubItems.length > 0) {
            return {
              ...item,
              items: filteredSubItems
            };
          }
        } else if (item.name.toLowerCase().includes(query.toLowerCase())) {
          return item;
        }
        return null;
      })
      .filter(Boolean);
  };

  let filteredCategories = tableTreeExplorer;

  if (searchQuery) {
    if (tableTypeToFilter === "rdbms") {
      filteredCategories = tableTreeExplorer.filter((item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    } else {
      filteredCategories = tableTreeExplorer
        .map((category) => {
          if (category.name.toLowerCase().includes(searchQuery.toLowerCase())) {
            return category;
          }
          const filteredItems = filterItems(category.items, searchQuery);

          if (filteredItems.length > 0) {
            return {
              ...category,
              items: filteredItems
            };
          }
          return null;
        })
        .filter(Boolean);
    }
  }

  if (tableTreeExplorer.length === 0) return <div>No Data found</div>;

  return (
    <div className="collapseable_popup_files">
      {getSelectedFilePath()}
      <div className="d_flex align_center mb_2">
        <div className="position_relative">
          <Input
            placeholder="Search"
            containerClassName="ml_auto"
            inputBoxStyle={{ paddingLeft: "26px", width: "76%" }}
            containerStyle={{ width: "96%" }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <MaterialIcon name="search" className="job_search" style={{ left: "12px", top: "7px" }} />
        </div>
        <div className="folder_collapse_icon" onClick={handleAllFolderCollapse}>
          {isFolderCollapse ? <MaterialIcon name="folder" /> : <MaterialIcon name="folder_open" />}
        </div>
      </div>
      <div className="overflow_scroll">
        <Tree
          explorerData={tableTreeExplorer}
          setExplorerData={setTableTreeExplorer}
          folderData={filteredCategories}
          callbackFunction={addingData}
          handleNewInsertFunction={handleNewInsert}
        />
      </div>
    </div>
  );
};

export default TableTreeModal;
